import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  Snackbar,
  Avatar,
  Autocomplete,
  TextField,
} from '@mui/material';
import { Grid } from '@mui/material';
import {
  Home,
  CalendarToday,
  History,
  ExpandMore,
  ExpandLess,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  Close as CloseIcon,
  Handshake as HandshakeIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { BottomNavigation } from '../layout/BottomNavigation';
import { useSafeArea } from '../hooks/useSafeArea';
import { TopBar } from '../layout/TopBar';
import { useNavigate, useParams } from 'react-router-dom';
import { useInventoryItem } from '../hooks/useInventoryItem';
import { useInventoryContext } from '../contexts/InventoryContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { EmptyInventoryState } from '../components/inventory/EmptyInventoryState';
import { getUserEvents, updateEvent, Event } from '../services/eventsService';
import { getUserGuests, Guest as GuestType } from '../services/guestsService';
import { sendLoanReminder, LoanReminderResult } from '../services/notificationsService';
import { isLoaned, isOverdue, buildLoan } from '../utils/loanStatus';

export const InventoryDetail: React.FC = () => {
  const { theme } = useTheme();
  const { user, hostlyUser } = useAuth();
  const safeArea = useSafeArea();
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId: string }>();
  const { item, usageHistory, loading, error, itemExists, refreshItem } = useInventoryItem(itemId);
  const { setItemLoan, returnItem } = useInventoryContext();
  const { isPremium, isPro } = useSubscription();
  const canLend = isPremium || isPro;
  const [showObservations, setShowObservations] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [addToEventDialog, setAddToEventDialog] = useState(false);
  const [userEvents, setUserEvents] = useState<Event[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [addingToEvents, setAddingToEvents] = useState(false);
  const [loanDialogOpen, setLoanDialogOpen] = useState(false);
  const [loanBusy, setLoanBusy] = useState(false);
  const sendingReminderRef = useRef(false);
  const [loanForm, setLoanForm] = useState({ borrowerName: '', quantity: '1', dueDate: '' });
  const [loanGuest, setLoanGuest] = useState<GuestType | null>(null);
  const [loanGuests, setLoanGuests] = useState<GuestType[]>([]);
  const itemIsLoaned = isLoaned(item);
  const loanOverdue = isOverdue(item?.loan);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatLoanMoment = (value?: string) => {
    if (!value) return '-';
    const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
    const d = dateOnly ? new Date(`${value}T00:00:00`) : new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    const datePart = d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
    if (dateOnly) return datePart;
    const timePart = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    return `${datePart} - ${timePart}`;
  };

  const handleCreateLoan = async () => {
    if (!item) return;
    const result = buildLoan({
      borrowerName: loanGuest ? `${loanGuest.name} ${loanGuest.lastName || ''}`.trim() : loanForm.borrowerName,
      borrowerGuestId: loanGuest?.id,
      borrowerGroup: loanGuest?.location,
      borrowerPhone: loanGuest?.phone,
      borrowerEmail: loanGuest?.email,
      quantity: loanForm.quantity,
      dueDate: loanForm.dueDate,
      item,
    });
    if (!result.ok) {
      setSnackbar({ open: true, message: 'Informe para quem está emprestando.', severity: 'error' });
      return;
    }
    setLoanBusy(true);
    try {
      await setItemLoan(item.id, result.loan);
      await refreshItem();
      setLoanDialogOpen(false);
      setLoanForm({ borrowerName: '', quantity: '1', dueDate: '' });
      setLoanGuest(null);
      setSnackbar({ open: true, message: 'Item marcado como emprestado.', severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message || 'Erro ao emprestar', severity: 'error' });
    } finally {
      setLoanBusy(false);
    }
  };

  const handleSendReminder = async () => {
    if (!item?.loan) return;
    if (sendingReminderRef.current) return;
    sendingReminderRef.current = true;
    setLoanBusy(true);
    try {
      const loan = item.loan;
      const dueText = loan.dueDate ? ` até ${formatLoanMoment(loan.dueDate)}` : '';
      const message = `Olá, ${loan.borrowerName}! Não esqueça de devolver o item "${item.itemName}"${dueText}.`;
      const senderName = [hostlyUser?.name, hostlyUser?.lastName].filter(Boolean).join(' ').trim() || 'Hostly';

      const opStorageKey = `loanReminderOp_${item.id}_${loan.loanId || loan.loanedAt || ''}`;
      let operationId = '';
      try {
        operationId = localStorage.getItem(opStorageKey) || '';
      } catch {
        operationId = '';
      }
      if (!operationId) {
        operationId = `op_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
        try {
          localStorage.setItem(opStorageKey, operationId);
        } catch {}
      }

      let viaWhats = false;
      if (loan.borrowerPhone) {
        const digits = loan.borrowerPhone.replace(/\D/g, '');
        const phone = digits.length <= 11 ? `55${digits}` : digits;
        window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
        viaWhats = true;
      }

      let hostlyResult: LoanReminderResult | null = null;
      if (loan.borrowerEmail) {
        try {
          hostlyResult = await sendLoanReminder(loan.borrowerEmail, user?.uid || '', senderName, item.itemName, message, {
            itemId: item.id,
            loanId: loan.loanId,
            loanedAt: loan.loanedAt,
            operationId,
          });
        } catch {
          hostlyResult = 'error';
        }
      }
      const viaHostly = hostlyResult === 'sent' || hostlyResult === 'already';

      if (hostlyResult !== 'error') {
        try {
          localStorage.removeItem(opStorageKey);
        } catch {}
      }

      const feedback: { message: string; severity: 'success' | 'error' | 'info' } =
        viaWhats && viaHostly
          ? { message: 'Lembrete enviado por WhatsApp e Hostly.', severity: 'success' }
          : viaWhats
          ? { message: 'Lembrete enviado por WhatsApp.', severity: 'success' }
          : viaHostly
          ? { message: 'Lembrete enviado pelo Hostly.', severity: 'success' }
          : hostlyResult === 'no_account'
          ? { message: 'Essa pessoa ainda não usa o Hostly. Cadastre um telefone para avisar por WhatsApp.', severity: 'info' }
          : hostlyResult === 'error'
          ? { message: 'Não foi possível enviar o lembrete pelo Hostly. Tente novamente.', severity: 'error' }
          : { message: 'Sem telefone ou email para avisar essa pessoa.', severity: 'info' };
      setSnackbar({ open: true, ...feedback });
    } finally {
      sendingReminderRef.current = false;
      setLoanBusy(false);
    }
  };

  const handleReturn = async () => {
    if (!item) return;
    setLoanBusy(true);
    try {
      await returnItem(item.id, {
        loanId: item.loan?.loanId,
        loanedAt: item.loan?.loanedAt,
        borrowerName: item.loan?.borrowerName,
      });
      await refreshItem();
      setSnackbar({ open: true, message: 'Item devolvido.', severity: 'success' });
    } catch (e: any) {
      await refreshItem();
      setSnackbar({ open: true, message: e?.message || 'Erro ao registrar devolução', severity: 'error' });
    } finally {
      setLoanBusy(false);
    }
  };

  // Carregar eventos do usuário quando o dialog for aberto
  useEffect(() => {
    const loadUserEvents = async () => {
      if (!user?.uid || !addToEventDialog) return;

      try {
        setLoadingEvents(true);
        const events = await getUserEvents(user.uid);
        const futureEvents = events.filter(event => {
          const eventDate = new Date(event.date);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return eventDate >= today;
        });
        setUserEvents(futureEvents);

        if (item) {
          const eventsWithItem = futureEvents
            .filter(event => event.inventoryItems?.includes(item.id))
            .map(event => event.id);
          setSelectedEvents(eventsWithItem);
        }
      } catch (error) {
        console.error('Erro ao carregar eventos:', error);
        setSnackbar({
          open: true,
          message: 'Erro ao carregar eventos',
          severity: 'error',
        });
      } finally {
        setLoadingEvents(false);
      }
    };

    loadUserEvents();
  }, [user?.uid, addToEventDialog, item]);

  useEffect(() => {
    const loadGuests = async () => {
      if (!loanDialogOpen || !user?.uid) return;
      try {
        setLoanGuests(await getUserGuests(user.uid));
      } catch {
        setLoanGuests([]);
      }
    };
    loadGuests();
  }, [loanDialogOpen, user?.uid]);

  const handleOpenAddToEvent = () => {
    if (!item) return;
    setAddToEventDialog(true);
  };

  const handleToggleEvent = (eventId: string) => {
    setSelectedEvents(prev => {
      if (prev.includes(eventId)) {
        return prev.filter(id => id !== eventId);
      } else {
        return [...prev, eventId];
      }
    });
  };

  const handleAddToEvents = async () => {
    if (!item || !user?.uid) return;

    try {
      setAddingToEvents(true);

      const updatePromises = userEvents.map(async (event) => {
        const currentItems = event.inventoryItems || [];
        const hasItem = currentItems.includes(item.id);
        const shouldHaveItem = selectedEvents.includes(event.id);

        if (hasItem && !shouldHaveItem) {
          const updatedItems = currentItems.filter(id => id !== item.id);
          await updateEvent(event.id, { inventoryItems: updatedItems });
        } else if (!hasItem && shouldHaveItem) {
          const updatedItems = [...currentItems, item.id];
          await updateEvent(event.id, { inventoryItems: updatedItems });
        }
      });

      await Promise.all(updatePromises);

      const selectedCount = selectedEvents.length;
      setSnackbar({
        open: true,
        message: selectedCount > 0
          ? `Item vinculado a ${selectedCount} evento(s) com sucesso!`
          : 'Item removido dos eventos',
        severity: 'success',
      });
      setAddToEventDialog(false);
    } catch (error) {
      console.error('Erro ao vincular item aos eventos:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao vincular item aos eventos',
        severity: 'error',
      });
    } finally {
      setAddingToEvents(false);
    }
  };

  if (loading) {
    return (
      <>
        <TopBar title="Acervo" icon={<Home sx={{ fontSize: '0.875rem' }} />} showBackButton onBackClick={() => navigate('/inventory')} />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress sx={{ color: theme.colors.primary }} />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <TopBar title="Acervo" icon={<Home sx={{ fontSize: '0.875rem' }} />} showBackButton onBackClick={() => navigate('/inventory')} />
        <Box sx={{ p: 3, paddingTop: '100px' }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </>
    );
  }

  if (!itemExists || !item || !itemId) {
    return (
      <>
        <TopBar
          title="Acervo"
          icon={<Home sx={{ fontSize: '0.875rem' }} />}
          showBackButton={true}
          onBackClick={() => navigate('/inventory')}
        />
        <Box sx={{ paddingTop: '70px', paddingBottom: '120px' }}>
          <EmptyInventoryState
            title="Item não encontrado"
            description="Este item não existe ou foi removido. Que tal adicionar um novo item ao seu acervo?"
            showDialog={true}
          />
        </Box>
        <BottomNavigation />
      </>
    );
  }

  return (
    <>
      <TopBar
        title={itemIsLoaned ? 'Emprestados' : 'Acervo'}
        icon={itemIsLoaned ? <HandshakeIcon sx={{ fontSize: '0.875rem' }} /> : <Home sx={{ fontSize: '0.875rem' }} />}
        showBackButton={true}
        showProfileButton={true}
        onBackClick={() => navigate(-1)}
      />
      <Box sx={{
        paddingBottom: '120px',
        paddingTop: '70px',
        maxWidth: '400px',
        margin: '0 auto',
        minHeight: '100vh',
      }}>
        {/* Image Section with Carousel */}
        <Box sx={{ position: 'relative' }}>
          {(() => {
            const photos = [item.photoUrl, (item as any).photoUrl2].filter(Boolean);
            const currentPhoto = photos[currentPhotoIndex] || null;

            return (
              <>
                <Box
                  onClick={() => currentPhoto && setImageDialogOpen(true)}
                  sx={{
                    width: '100%',
                    height: 300,
                    backgroundImage: currentPhoto ? `url(${currentPhoto})` : 'linear-gradient(135deg, #E1C4B5 0%, #D4A574 100%)',
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: currentPhoto ? 'pointer' : 'default',
                    position: 'relative',
                  }}
                >
                  {!currentPhoto && (
                    <Typography sx={{ color: 'white', fontSize: '3rem', opacity: 0.5 }}>
                      📦
                    </Typography>
                  )}

                  {/* Navigation Arrows */}
                  {photos.length > 1 && (
                    <>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
                        }}
                        sx={{
                          position: 'absolute',
                          left: 8,
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          color: 'white',
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
                        }}
                      >
                        <ChevronLeft />
                      </IconButton>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
                        }}
                        sx={{
                          position: 'absolute',
                          right: 8,
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          color: 'white',
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
                        }}
                      >
                        <ChevronRight />
                      </IconButton>

                      {/* Photo indicator */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          display: 'flex',
                          gap: 1,
                        }}
                      >
                        {photos.map((_, index) => (
                          <Box
                            key={index}
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: index === currentPhotoIndex ? 'white' : 'rgba(255, 255, 255, 0.5)',
                              transition: 'all 0.3s',
                            }}
                          />
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              </>
            );
          })()}
          <IconButton
            sx={{
              position: 'absolute',
              bottom: 16,
              right: 16,
              backgroundColor: theme.colors.accent,
              color: theme.colors.text.primary,
              width: 48,
              height: 48,
              boxShadow: theme.shadows.medium,
              '&:hover': {
                backgroundColor: theme.colors.accent,
                opacity: 0.8,
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              const photos = [item?.photoUrl, (item as any)?.photoUrl2].filter(Boolean);
              if (photos.length > 0) {
                setImageDialogOpen(true);
              }
            }}
          >
            <ImageIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{
          backgroundColor: theme.colors.background,
          borderRadius: `${theme.radii.large} ${theme.radii.large} 0 0`,
          marginTop: '-24px',
          position: 'relative',
          zIndex: 1,
          padding: 3,
        }}>
          <Typography sx={{
            color: theme.colors.text.secondary,
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            marginBottom: 0.5,
          }}>
            Itens
          </Typography>
          <Typography sx={{
            color: theme.colors.primary,
            fontSize: { xs: '1.5rem', sm: '1.875rem' },
            fontWeight: 'bold',
            marginBottom: 1.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {item.itemName}
          </Typography>
          {itemIsLoaned && item.loan && (
            <Box sx={{ backgroundColor: '#d32f2f', color: '#fff', borderRadius: theme.radii.small, px: 1.5, py: 0.75, mb: 2, display: 'inline-block' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.2 }}>Atenção:</Typography>
              <Typography sx={{ fontSize: '0.75rem' }}>
                Você tem {item.loan.quantity} unidade(s) dessa peça emprestada(s).
              </Typography>
            </Box>
          )}
          {item.description && (
            <Typography sx={{
              color: theme.colors.text.primary,
              marginBottom: 3,
              fontSize: { xs: '0.875rem', sm: '1rem' },
            }}>
              Descrição: {item.description}
            </Typography>
          )}

          {/* Code and Update Date */}
          <Grid container spacing={3} sx={{ marginBottom: 3, paddingBottom: 3, borderBottom: `1px solid ${theme.colors.border}` }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography sx={{
                color: theme.colors.text.secondary,
                fontWeight: 600,
                marginBottom: 0.5,
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}>
                Código do item
              </Typography>
              <Typography sx={{
                fontSize: { xs: '1.1rem', sm: '1.5rem' },
                fontWeight: 300,
                color: theme.colors.text.primary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {item.id.substring(0, 8).toUpperCase()}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: theme.colors.text.secondary, mt: 0.25 }}>
                Identificador interno para referência
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography sx={{
                color: theme.colors.text.secondary,
                fontWeight: 600,
                marginBottom: 0.5,
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}>
                Atualizado em:
              </Typography>
              <Typography sx={{
                color: theme.colors.text.primary,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {formatDate(item.updatedAt)}
              </Typography>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Grid container spacing={2} sx={{ marginBottom: 3 }}>
            <Grid size={{ xs: 6, sm: 6 }}>
              <Card
                onClick={handleOpenAddToEvent}
                sx={{
                  backgroundColor: theme.colors.card,
                  borderRadius: theme.radii.medium,
                  border: 'none',
                  boxShadow: 'none',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: `${theme.colors.primary}10`,
                  },
                }}
              >
                <CardContent sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  padding: { xs: 1.5, sm: 2 },
                  '&:last-child': { paddingBottom: { xs: 1.5, sm: 2 } },
                }}>
                  <Box sx={{
                    backgroundColor: theme.colors.primary,
                    color: theme.colors.text.contrast,
                    borderRadius: '50%',
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 48 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <CalendarToday sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                  </Box>
                  <Typography sx={{
                    fontWeight: 600,
                    color: theme.colors.text.primary,
                    textAlign: 'center',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}>
                    Adicionar a um evento
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, sm: 6 }}>
              <Card
                onClick={() => navigate(`/inventory/${itemId}/history`)}
                sx={{
                  backgroundColor: theme.colors.card,
                  borderRadius: theme.radii.medium,
                  border: 'none',
                  boxShadow: 'none',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: `${theme.colors.primary}10`,
                  },
                }}
              >
                <CardContent sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  padding: { xs: 1.5, sm: 2 },
                  '&:last-child': { paddingBottom: { xs: 1.5, sm: 2 } },
                }}>
                  <Box sx={{
                    backgroundColor: theme.colors.primary,
                    color: theme.colors.text.contrast,
                    borderRadius: '50%',
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 48 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <History sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                  </Box>
                  <Typography sx={{
                    fontWeight: 600,
                    color: theme.colors.text.primary,
                    textAlign: 'center',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}>
                    Histórico de uso ({usageHistory.length})
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, sm: 6 }}>
              <Card
                onClick={() => {
                  if (!canLend) {
                    setSnackbar({ open: true, message: 'Empréstimos disponíveis a partir do plano Basic.', severity: 'info' });
                    navigate('/pricing');
                    return;
                  }
                  setLoanDialogOpen(true);
                }}
                sx={{
                  backgroundColor: theme.colors.card,
                  borderRadius: theme.radii.medium,
                  border: 'none',
                  boxShadow: 'none',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: `${theme.colors.primary}10` },
                }}
              >
                <CardContent sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  padding: { xs: 1.5, sm: 2 },
                  '&:last-child': { paddingBottom: { xs: 1.5, sm: 2 } },
                }}>
                  <Box sx={{
                    backgroundColor: theme.colors.primary,
                    color: theme.colors.text.contrast,
                    borderRadius: '50%',
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 48 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <HandshakeIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                  </Box>
                  <Typography sx={{
                    fontWeight: 600,
                    color: theme.colors.text.primary,
                    textAlign: 'center',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}>
                    Emprestar
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {itemIsLoaned && item.loan && (
            <Box sx={{ mb: 3 }}>
              <Card sx={{ border: `1px solid ${theme.colors.border}`, borderRadius: theme.radii.medium, boxShadow: 'none', backgroundColor: theme.colors.card }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Typography variant="caption" sx={{ color: theme.colors.text.secondary, fontWeight: 600 }}>Emprestado em:</Typography>
                      <Typography sx={{ color: theme.colors.text.primary }}>{formatLoanMoment(item.loan.loanedAt)}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" sx={{ color: theme.colors.text.secondary, fontWeight: 600 }}>Para:</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                        <Avatar sx={{ width: 28, height: 28, bgcolor: theme.colors.secondary, color: theme.colors.text.onSecondary, fontSize: '0.8rem' }}>
                          {(item.loan.borrowerName || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ color: theme.colors.text.primary, lineHeight: 1.2 }}>{item.loan.borrowerName}</Typography>
                          {item.loan.borrowerGroup && (
                            <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>Grupo: {item.loan.borrowerGroup}</Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" sx={{ color: theme.colors.text.secondary, fontWeight: 600 }}>Quantidade:</Typography>
                      <Typography sx={{ color: theme.colors.text.primary }}>{item.loan.quantity} unidade(s)</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" sx={{ color: theme.colors.text.secondary, fontWeight: 600 }}>Data para devolução:</Typography>
                      <Typography sx={{ color: loanOverdue ? '#d32f2f' : theme.colors.text.primary, fontWeight: loanOverdue ? 700 : 400 }}>
                        {item.loan.dueDate ? formatLoanMoment(item.loan.dueDate) : 'Sem data'}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSendReminder}
                    disabled={loanBusy}
                    sx={{ mt: 2, backgroundColor: theme.colors.secondary, color: theme.colors.text.onSecondary, textTransform: 'none' }}
                  >
                    Enviar lembrete de devolução
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleReturn}
                    disabled={loanBusy}
                    sx={{ mt: 1, backgroundColor: theme.colors.secondary, color: theme.colors.text.onSecondary, textTransform: 'none' }}
                  >
                    {loanBusy ? <CircularProgress aria-label="Carregando" size={20} sx={{ color: theme.colors.text.onSecondary }} /> : 'Marcar como devolvido'}
                  </Button>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Details Grid */}
          <Grid container spacing={3} sx={{ marginBottom: 3 }}>
            {[
              { label: 'Quantidade', value: item.quantity?.toString() || '0' },
              { label: 'Onde comprei', value: item.store || 'Não informado' },
              { label: 'Valor unitário', value: item.unitValue ? formatCurrency(item.unitValue) : 'Não informado' },
              { label: 'Categoria', value: item.category || 'Sem categoria' },
              { label: 'Marca', value: item.brand || 'Não informado' },
              { label: 'Medidas', value: item.measurements || 'Não informado' },
            ].map((detail, index) => (
              <Grid key={index} size={6}>
                <Box sx={{
                  paddingY: 1.5,
                  borderBottom: `1px solid ${theme.colors.border}`,
                }}>
                  <Typography sx={{
                    color: theme.colors.text.secondary,
                    fontWeight: 600,
                    marginBottom: 0.5,
                    fontSize: '0.875rem',
                  }}>
                    {detail.label}
                  </Typography>
                  <Typography sx={{
                    fontSize: '1.125rem',
                    color: theme.colors.text.primary,
                  }}>
                    {detail.value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Observations */}
          <Box sx={{
            borderTop: `1px solid ${theme.colors.border}`,
            paddingTop: 2,
          }}>
            <Button
              fullWidth
              onClick={() => setShowObservations(!showObservations)}
              sx={{
                justifyContent: 'space-between',
                color: theme.colors.text.primary,
                textTransform: 'none',
                fontSize: '1.125rem',
                fontWeight: 600,
                padding: 1,
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <span>Observações</span>
              {showObservations ? <ExpandLess /> : <ExpandMore />}
            </Button>
            <Collapse in={showObservations}>
              <Box sx={{
                marginTop: 2,
                padding: 2,
                backgroundColor: theme.colors.card,
                borderRadius: theme.radii.medium,
              }}>
                <Typography sx={{ color: theme.colors.text.primary }}>
                  {item.observations || 'Nenhuma observação cadastrada.'}
                </Typography>
              </Box>
            </Collapse>
          </Box>
        </Box>

        {/* Dialog de adicionar a eventos */}
        <Dialog
          open={addToEventDialog}
          onClose={() => !addingToEvents && setAddToEventDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ color: theme.colors.text.primary }}>
            Adicionar a Eventos
          </DialogTitle>
          <DialogContent>
            {loadingEvents ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress sx={{ color: theme.colors.primary }} />
              </Box>
            ) : userEvents.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography sx={{ color: theme.colors.text.secondary }}>
                  Você não possui eventos futuros.
                </Typography>
                <Button
                  onClick={() => {
                    setAddToEventDialog(false);
                    navigate('/events');
                  }}
                  sx={{ mt: 2, color: theme.colors.primary }}
                >
                  Criar Evento
                </Button>
              </Box>
            ) : (
              <>
                <Typography sx={{ color: theme.colors.text.secondary, mb: 2 }}>
                  Selecione os eventos onde deseja usar este item:
                </Typography>
                <List>
                  {userEvents.map((event) => {
                    const eventDate = new Date(event.date);
                    const formattedDate = eventDate.toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                    });

                    return (
                      <ListItemButton
                        key={event.id}
                        onClick={() => handleToggleEvent(event.id)}
                        sx={{
                          borderRadius: theme.radii.small,
                          mb: 1,
                          border: `1px solid ${theme.colors.border}`,
                        }}
                      >
                        <Checkbox
                          checked={selectedEvents.includes(event.id)}
                          sx={{
                            color: theme.colors.primary,
                            '&.Mui-checked': {
                              color: theme.colors.primary,
                            },
                          }}
                        />
                        <ListItemText
                          primary={event.title}
                          secondary={`${formattedDate} às ${event.time}`}
                          sx={{
                            '& .MuiListItemText-primary': {
                              color: theme.colors.text.primary,
                              fontWeight: 600,
                            },
                            '& .MuiListItemText-secondary': {
                              color: theme.colors.text.secondary,
                            },
                          }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setAddToEventDialog(false)}
              disabled={addingToEvents}
              sx={{ color: theme.colors.text.secondary }}
            >
              Cancelar
            </Button>
            {userEvents.length > 0 && (
              <Button
                onClick={handleAddToEvents}
                disabled={addingToEvents}
                variant="contained"
                sx={{
                  backgroundColor: theme.colors.primary,
                  color: theme.colors.text.contrast,
                  '&:hover': {
                    backgroundColor: theme.colors.primary,
                    opacity: 0.8,
                  },
                }}
              >
                {addingToEvents ? (
                  <CircularProgress size={20} sx={{ color: 'inherit' }} />
                ) : (
                  `Confirmar (${selectedEvents.length})`
                )}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <Dialog open={loanDialogOpen} onClose={() => !loanBusy && setLoanDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>Emprestar item</DialogTitle>
          <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <Autocomplete
              options={loanGuests}
              value={loanGuest}
              onChange={(_, value) => setLoanGuest(value)}
              getOptionLabel={(g) => `${g.name} ${g.lastName || ''}`.trim()}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                <TextField {...params} label="Para quem (convidado)" size="small" autoFocus />
              )}
              noOptionsText="Nenhum convidado cadastrado"
            />
            {!loanGuest && (
              <TextField
                label="Ou digite um nome"
                value={loanForm.borrowerName}
                onChange={(e) => setLoanForm({ ...loanForm, borrowerName: e.target.value })}
                size="small"
                fullWidth
                helperText="Escolher um convidado permite enviar o lembrete por WhatsApp ou Hostly"
              />
            )}
            <TextField
              label="Quantidade"
              value={loanForm.quantity}
              onChange={(e) => setLoanForm({ ...loanForm, quantity: e.target.value.replace(/\D/g, '') })}
              size="small"
              fullWidth
              inputProps={{ inputMode: 'numeric' }}
              helperText={item?.quantity ? `Disponível: ${item.quantity}` : undefined}
            />
            <TextField
              label="Devolver até (opcional)"
              type="datetime-local"
              value={loanForm.dueDate}
              onChange={(e) => setLoanForm({ ...loanForm, dueDate: e.target.value })}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setLoanDialogOpen(false)} disabled={loanBusy} sx={{ color: theme.colors.text.secondary }}>Cancelar</Button>
            <Button variant="contained" onClick={handleCreateLoan} disabled={loanBusy || (!loanGuest && !loanForm.borrowerName.trim())} sx={{ backgroundColor: theme.colors.primary }}>
              {loanBusy ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Emprestar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Dialog
          open={imageDialogOpen}
          onClose={() => setImageDialogOpen(false)}
          maxWidth={false}
          fullScreen
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
            },
          }}
        >
          <IconButton
            onClick={() => setImageDialogOpen(false)}
            sx={{
              position: 'absolute',
              top: `calc(16px + ${safeArea.top}px)`,
              right: `calc(16px + ${safeArea.right}px)`,
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              zIndex: 2,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            {(() => {
              const photos = [item?.photoUrl, (item as any)?.photoUrl2].filter(Boolean);
              const currentPhoto = photos[currentPhotoIndex];

              return (
                <>
                  {currentPhoto && (
                    <Box
                      component="img"
                      src={currentPhoto}
                      alt={item?.itemName}
                      sx={{
                        maxWidth: '90%',
                        maxHeight: '90%',
                        objectFit: 'contain',
                      }}
                    />
                  )}
                  {photos.length > 1 && (
                    <>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
                        }}
                        sx={{
                          position: 'absolute',
                          left: `calc(16px + ${safeArea.left}px)`,
                          color: 'white',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          zIndex: 2,
                          width: 56,
                          height: 56,
                          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' },
                        }}
                      >
                        <ChevronLeft fontSize="large" />
                      </IconButton>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
                        }}
                        sx={{
                          position: 'absolute',
                          right: `calc(16px + ${safeArea.right}px)`,
                          color: 'white',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          zIndex: 2,
                          width: 56,
                          height: 56,
                          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' },
                        }}
                      >
                        <ChevronRight fontSize="large" />
                      </IconButton>
                    </>
                  )}
                </>
              );
            })()}
          </Box>
        </Dialog>

        <BottomNavigation />
      </Box>
    </>
  );
};
